import {
  Dispatch, SetStateAction, useCallback, useState,
} from 'react';
import { upperFirst } from 'lodash';
import {
  useHeroSnackbar,
  useTable,
  useQueryTableFetch,
  useTranslations,
  useTableLocalization,
  useFilterButton,
  useTablePreserverActivePageValidator,
  useTablePreserverDecoder,
  useTablePreserverEncoder,
  usePubSub,
  buildTablePreserverDecoderConfigItem,
  TablePreserverCommonInitialTableValues,
  IUseTableReturn,
  IUseTableResetTableMethodsReturn,
  IUseQueryTableFetchReturn,
  IUseTableLocalizationReturn,
  IUseFilterButtonReturn,
  TUseTablePreserverDecoderConfigs,
  TablePreserverDecoderConfigItemTypeEnum,
  TSetSearchParams,
  TSecurityScoreName,
  TPasswordStrengthName,
  getSecurityScoreVaultsStrength,
  getPasswordStrengthValues,
} from '@uniqkey-frontend/shared-app';
import {
  VaultOrderProperty,
  Password2FaStatus,
} from '@uniqkey-backend-organization-web/api-client';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import {
  IGetVaultPasswordsParams,
  TGetVaultPasswordsMethod,
} from '../../useVaultsAPI/interfaces';
import {
  IOrganizationLoginsTabFilterSubmitResult,
// eslint-disable-next-line max-len
} from '../../../pages/AllSecuredDataPage/components/OrganizationLoginsTab/components/OrganizationLoginsTabFilter';
import { IOrganizationLoginsTableRow } from '../../../components/tables/OrganizationLoginsTable';
import useVaultsAPI from '../../useVaultsAPI';
import tablePreservationConfigs from '../../../helpers/tablePreservation/configs';
import PubSubEventEnum from '../../../enums/PubSubEventEnum';
import { REACT_QUERY_LOGINS_KEY } from '../useEmployeeGroupLoginsTable';
import { TReusedPassword } from '../../../helpers/filters/interfaces';
import ReusedPasswordEnum from '../../../enums/ReusedPasswordEnum';

export interface IUseOrganizationLoginsTableParams {
  noDataMessageKey: string;
}

export interface IUseOrganizationLoginsTableReturn extends
  Omit<IUseTableReturn<IOrganizationLoginsTableRow>, 'selectedRows' | 'resetTableMethods'>,
  Pick<IUseTableResetTableMethodsReturn, 'resetActivePage' | 'resetSelectedRows'>,
  Omit<IUseQueryTableFetchReturn<TGetVaultPasswordsMethod>, 'data' | 'isFetchedAfterMount'>,
  IUseFilterButtonReturn
{
  logins: IUseQueryTableFetchReturn<TGetVaultPasswordsMethod>['data'];
  selectedLogins: IUseTableReturn<IOrganizationLoginsTableRow>['selectedRows'];
  localization: IUseTableLocalizationReturn['localization'];
  searchQuery: IGetVaultPasswordsParams['searchQuery'];
  setSearchQuery: Dispatch<SetStateAction<IGetVaultPasswordsParams['searchQuery']>>;
  filterValues: IOrganizationLoginsTabFilterSubmitResult;
  setFilterValues: Dispatch<SetStateAction<IOrganizationLoginsTabFilterSubmitResult>>;
}

interface ITablePreservationConfigs extends
  Omit<IGetVaultPasswordsParams,
    'page' | 'pageLength' | 'groupId' | 'employeeAccountId' | 'orderPropertyName' | 'isDescending'
    | 'minStrength' | 'maxStrength' | 'minSecurityScore' | 'maxSecurityScore' | 'isReusedByUser'
    | 'isReusedByOrganization' | 'isPasswordEmpty'
  >,
  Pick<IUseTableReturn<IOrganizationLoginsTableRow>, 'columnOrderBy' | 'columnOrderDirection'>
{
  activePage: IGetVaultPasswordsParams['page'];
  perPage: IGetVaultPasswordsParams['pageLength'];
  securityScoreName: TSecurityScoreName;
  passwordStrengthName: TPasswordStrengthName;
  reusedPassword: TReusedPassword;
}

const PRESERVATION_CONFIGS: TUseTablePreserverDecoderConfigs<ITablePreservationConfigs> = {
  activePage: true,
  perPage: true,
  searchQuery: true,
  columnOrderBy: buildTablePreserverDecoderConfigItem(
    TablePreserverDecoderConfigItemTypeEnum.ENUM,
    { enumToCheck: VaultOrderProperty },
  ),
  columnOrderDirection: true,
  securityScoreName: true,
  passwordStrengthName: true,
  ownership: tablePreservationConfigs.ownership,
  applicationName: true,
  password2FAStatus: buildTablePreserverDecoderConfigItem(
    TablePreserverDecoderConfigItemTypeEnum.ENUM,
    { enumToCheck: Password2FaStatus },
  ),
  reusedPassword: buildTablePreserverDecoderConfigItem(
    TablePreserverDecoderConfigItemTypeEnum.ENUM,
    { enumToCheck: ReusedPasswordEnum },
  ),
};

const useOrganizationLoginsTable = (
  params: IUseOrganizationLoginsTableParams,
): IUseOrganizationLoginsTableReturn => {
  const {
    noDataMessageKey,
  } = params;

  const queryClient = useQueryClient();
  const { t } = useTranslations();
  const { showError } = useHeroSnackbar();
  const { getVaultPasswords } = useVaultsAPI();
  const [searchParams, setSearchParams] = useSearchParams();

  const handleOnRequestError = useCallback(() => {
    showError({ text: t('common.somethingWentWrong') });
  }, [showError, t]);

  const {
    activePage: initialActivePage,
    perPage: initialPerPage,
    searchQuery: initialSearchQuery,
    columnOrderBy: initialColumnOrderBy,
    columnOrderDirection: initialColumnOrderDirection,
    securityScoreName: initialSecurityScoreName,
    passwordStrengthName: initialPasswordStrengthName,
    ownership: initialOwnership,
    applicationName: initialApplicationName,
    password2FAStatus: initialPassword2FAStatus,
    reusedPassword: initialReusedPassword,
  } = useTablePreserverDecoder<ITablePreservationConfigs>(searchParams, PRESERVATION_CONFIGS);

  const [searchQuery, setSearchQuery] = useState<
    IGetVaultPasswordsParams['searchQuery']
  >(() => initialSearchQuery);
  const [filterValues, setFilterValues] = useState<
    IOrganizationLoginsTabFilterSubmitResult
  >({
    ownership: initialOwnership,
    applicationName: initialApplicationName,
    securityScoreName: initialSecurityScoreName,
    passwordStrengthName: initialPasswordStrengthName,
    password2FAStatus: initialPassword2FAStatus,
    reusedPassword: initialReusedPassword,
  });

  const { isFilterActive, numberOfActiveFilters } = useFilterButton(filterValues);

  const {
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
    selectedRows: selectedLogins,
    resetTableMethods,
    ...restTableProps
  } = useTable<IOrganizationLoginsTableRow>({
    initialActivePage,
    initialPerPage,
    initialColumnOrderBy,
    initialColumnOrderDirection,
  });

  const {
    resetTable,
    resetActivePage,
    resetSelectedRows,
  } = resetTableMethods;

  const handleTableReset = useCallback(() => {
    resetTable();
    setSearchQuery(TablePreserverCommonInitialTableValues.searchQuery);
    setFilterValues({
      ownership: undefined,
      applicationName: undefined,
      securityScoreName: TablePreserverCommonInitialTableValues.securityScoreName,
      passwordStrengthName: TablePreserverCommonInitialTableValues.passwordStrengthName,
      password2FAStatus: undefined,
      reusedPassword: undefined,
    });
  }, [resetTable]);
  usePubSub(PubSubEventEnum.RESET_TABLE, handleTableReset);

  const {
    data: logins, isLoading, isFetchedAfterMount, total, resetQuery,
  } = useQueryTableFetch({
    queryKey: REACT_QUERY_LOGINS_KEY,
    queryClient,
    request: getVaultPasswords,
    params: {
      page: activePage,
      pageLength: perPage,
      ownership: filterValues.ownership,
      applicationName: filterValues.applicationName,
      ...getSecurityScoreVaultsStrength(filterValues.securityScoreName),
      ...getPasswordStrengthValues(filterValues.passwordStrengthName),
      searchQuery,
      orderPropertyName: VaultOrderProperty[
        upperFirst(columnOrderBy) as keyof typeof VaultOrderProperty
      ],
      isDescending: columnOrderDirection ? columnOrderDirection === 'desc' : undefined,
      password2FAStatus: filterValues.password2FAStatus,
      isReusedByOrganization: filterValues.reusedPassword === ReusedPasswordEnum
        .IsReusedByOrganization || undefined,
      isReusedByUser: filterValues.reusedPassword === ReusedPasswordEnum
        .IsReusedByUser || undefined,
    },
    onRequestError: handleOnRequestError,
  });

  const { localization } = useTableLocalization({
    searchQuery,
    isFilterActive,
    noDataMessageKey,
    isLoading,
  });

  useTablePreserverActivePageValidator({
    activePage, resetActivePage, perPage, total, isFetchedAfterMount,
  });

  useTablePreserverEncoder<ITablePreservationConfigs>(setSearchParams as TSetSearchParams, {
    activePage,
    perPage,
    searchQuery,
    columnOrderBy,
    columnOrderDirection,
    ownership: filterValues.ownership,
    applicationName: filterValues.applicationName,
    securityScoreName: filterValues.securityScoreName,
    passwordStrengthName: filterValues.passwordStrengthName,
    password2FAStatus: filterValues.password2FAStatus,
    reusedPassword: filterValues.reusedPassword,
  });

  return {
    logins,
    isLoading,
    total,
    resetQuery,
    activePage,
    perPage,
    columnOrderBy,
    columnOrderDirection,
    selectedLogins,
    localization,
    searchQuery,
    setSearchQuery,
    filterValues,
    setFilterValues,
    isFilterActive,
    numberOfActiveFilters,
    resetActivePage,
    resetSelectedRows,
    ...restTableProps,
  };
};

export default useOrganizationLoginsTable;
